import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Trash2, Plus, Edit } from "lucide-react";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function PaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [name, setName] = useState("");
  const [type, setType] = useState("nakit");
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [editName, setEditName] = useState("");
  const [editType, setEditType] = useState("nakit");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchMethods = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/payment_methods`, { withCredentials: true });
      setMethods(data);
    } catch (e) {
      toast.error("Ödeme yöntemleri alınamadı.");
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/payment_methods`, { name, type }, { withCredentials: true });
      toast.success("Ödeme yöntemi oluşturuldu.");
      setName("");
      setType("nakit");
      fetchMethods();
    } catch (e) {
      toast.error("Oluşturulamadı.");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editName.trim()) return;
    try {
      await axios.put(`${BACKEND_URL}/api/payment_methods/${selectedMethod.id}`, { name: editName, type: editType }, { withCredentials: true });
      toast.success("Ödeme yöntemi güncellendi.");
      setEditModalOpen(false);
      fetchMethods();
    } catch (e) {
      toast.error("Güncellenemedi.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/payment_methods/${id}`, { withCredentials: true });
      toast.success("Ödeme yöntemi silindi.");
      fetchMethods();
    } catch (e) {
      toast.error("Silinemedi.");
    }
  };

  const getTypeLabel = (t) => {
    if (t === "kredi_karti") return "Kredi Kartı";
    if (t === "banka") return "Banka / Transfer";
    return "Nakit";
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex items-center justify-between border-b border-border pb-4">
        <h1 className="text-3xl font-heading font-bold">Ödeme Yöntemleri</h1>
      </div>

      <Card className="swiss-card p-6">
        <form onSubmit={handleCreate} className="flex gap-4 items-end flex-wrap">
          <div className="flex-1 min-w-[200px] space-y-2">
            <Label htmlFor="method-name">Yeni Ödeme Yöntemi Adı</Label>
            <Input
              id="method-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn: Garanti Kredi Kartı, Peşin"
              data-testid="method-name-input"
            />
          </div>
          <div className="w-40 space-y-2">
            <Label>Türü</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="method-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nakit">Nakit / Kasa</SelectItem>
                <SelectItem value="banka">Banka / Transfer</SelectItem>
                <SelectItem value="kredi_karti">Kredi Kartı</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" data-testid="create-method-button">
            <Plus size={16} className="mr-2" /> Ekle
          </Button>
        </form>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {methods.map(method => (
          <Card key={method.id} className="swiss-card flex items-center justify-between p-4">
            <div className="flex flex-col">
              <span className="font-medium" data-testid={`method-name-${method.id}`}>{method.name}</span>
              <span className={`text-xs uppercase tracking-widest mt-0.5 ${method.type === 'kredi_karti' ? 'text-blue-600' : method.type === 'banka' ? 'text-amber-600' : 'text-muted-foreground'}`}>
                {getTypeLabel(method.type)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-primary hover:bg-primary/10"
                onClick={() => {
                  setSelectedMethod(method);
                  setEditName(method.name);
                  setEditType(method.type || "nakit");
                  setEditModalOpen(true);
                }}
                data-testid={`edit-method-${method.id}`}
              >
                <Edit size={18} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:bg-destructive/10"
                onClick={() => {
                  toast("Emin misiniz?", {
                    action: {
                      label: "Evet, Sil",
                      onClick: () => handleDelete(method.id)
                    }
                  });
                }}
                data-testid={`delete-method-button-${method.id}`}
              >
                <Trash2 size={18} />
              </Button>
            </div>
          </Card>
        ))}
        {methods.length === 0 && (
          <div className="text-muted-foreground text-sm col-span-full">Kayıtlı ödeme yöntemi bulunamadı.</div>
        )}
      </div>

      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Ödeme Yöntemini Düzenle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Yöntem Adı</Label>
              <Input value={editName} onChange={e => setEditName(e.target.value)} required data-testid="edit-method-name" />
            </div>
            <div className="space-y-2">
              <Label>Türü</Label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger data-testid="edit-method-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nakit">Nakit / Kasa (Kasadan Düşer)</SelectItem>
                  <SelectItem value="banka">Banka / Transfer (Kasadan Düşmez)</SelectItem>
                  <SelectItem value="kredi_karti">Kredi Kartı (Kasadan Düşmez)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full h-10 mt-2" data-testid="save-edit-method-btn">Kaydet</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
